import React, { createContext, useState, useContext, useEffect } from "react";
import {FilterContextType, FilterDataProps, HandleFilterType } from "../types/filter.types"
import { FilterTypes } from "../types/product.types"
import { defaultFilters } from "../constants/filters";
import { useUserContext } from "./UserContext";
import { log } from "../utils/logger";

/**
* React context for managing product filters and recommendations.
*/
const FilterContext = createContext<FilterContextType | undefined>(undefined);

/** 
* Provider component that manages filter state and recommended products.
* 
* Provides:
* - Current filter data and basic filter type.
* - Functions to handle filter changes, unset filters, and manage basic filters.
* - Recommended products array and setter.
*/
export const FilterProvider = ({ children }: { children: React.ReactNode }) => {
    const { loggedIn } = useUserContext();
    
    const [basicFilter, setBasicFilter] = useState<FilterTypes>(() => {
    const saved = sessionStorage.getItem("catermatBasicFilter");
    return (saved as FilterTypes) || "";
    });

    useEffect(() => {
        sessionStorage.setItem("catermatBasicFilter", basicFilter);
    }, [basicFilter]);
    // Filter data
    const [filterData, setFilterData] = useState<FilterDataProps>(() => {
        const saved = sessionStorage.getItem("FilterData");
        return saved ? JSON.parse(saved) : defaultFilters;
    });
    // Recommended products based on cart data
    const [recommended, setRecommended] = useState<number[]>(() => {
        const saved = sessionStorage.getItem("recommendedProducts");
        return saved ? JSON.parse(saved) : [];
    });
    /**
    * Updates the filter data by key and value.
    * Resets the basic filter.
    * 
    * @param key - The filter key to update
    * @param value - The new filter value
    */
    const handleFilter: HandleFilterType  = (key, value) => {
        setFilterData((prev) => {
            const updated = { ...prev, [key]: value };
            sessionStorage.setItem("FilterData", JSON.stringify(updated));
            return updated;
        });
        // setBasicFilter(""); // Unset basic filter
    }
    // Unset filters
    const unset = () => {
        sessionStorage.setItem("FilterData", JSON.stringify(defaultFilters));
        setFilterData(defaultFilters);
    }
    /**
    * Toggles the basic filter. If selecting the current filter, it unsets it.
    * Also resets custom filters when a basic filter is set.
    * 
    * @param value - The basic filter to toggle
    */
    const handleBasicFilter = (value: FilterTypes) => {
        if (value === "" as any) return; // ignore accidental clears from UI
        setBasicFilter((prev) => (prev === value ? "" as any : value));
        unset(); // keep this if you want basic filter to wipe custom filters initially
    };

    useEffect(() => {
    //log("[Filter] basicFilter =>", basicFilter);
}, [basicFilter]);

    const setAllFilters = (next: FilterDataProps) => {
        setFilterData(next);
        sessionStorage.setItem("FilterData", JSON.stringify(next));
    };


    /*
    useEffect(()=>{
        log(filterData)
    },[filterData])
    */
    // Unset filters on logout
    useEffect(()=>{
        if(!loggedIn){
            unset();
        }
    },[loggedIn])

    return (
        <FilterContext.Provider value={{setAllFilters, filterData, handleFilter, unset, basicFilter, setBasicFilter, handleBasicFilter, recommended, setRecommended }}>
            {children}
        </FilterContext.Provider>
    );
};
/**
* Hook to access the FilterContext.
* Throws error if used outside of a FilterProvider.
* 
* @returns The filter context state and handlers.
*/
export const useFilterContext = () => {
    const context = useContext(FilterContext); 
    if (!context) throw new Error("useFilterContext must be used within a FilterProvider");
    return context;
};